<template>
  <div class="mapping">
    <ak-box
      v-if="available"
      :narrow="true"
      :rounded="true"
      background="grey-lightest"
    >
      <div class="mapping__entries">
        <div class="mapping__headers">
          <div>{{ sourceLabel }}</div>
          <div>Field to map to</div>
        </div>
        <div
          v-for="mapping in operation.configuration.mappings"
          :key="mapping.uuid"
          class="mapping__entry-container"
        >
          <ak-integration-s-f-t-p-create-lead-mapping-entry
            :mapping="mapping"
            :source-input="sourceInput"
            :source-input-props="sourceInputProps"
            :source-read-only="sourceReadOnly"
            :target-fields="leadFields"
            :target-fields-available="leadFieldsUnallocated"
            :lock-target="lockedMappingEntry(mapping.config.field)"
            @on-update-mapping-entry="
              updateMappingEntryHandler(mapping.uuid, $event)
            "
            @on-delete-mapping-entry="deleteMappingEntryHandler(mapping.uuid)"
          />
        </div>
      </div>
      <div class="mapping__actions">
        <ak-button @click="addMappingEntry()">Add Entry</ak-button>
      </div>
    </ak-box>
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue'

import debounce from 'lodash.debounce'
import fileTypes from '../../constants/fileTypes'

import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import IntegrationSFTPCreateLeadMappingEntry from './IntegrationSFTPCreateLeadMappingEntry.vue'

export default defineComponent({
  name: 'AkIntegrationSFTPCreateLeadMapping',
  components: {
    [Button.name]: Button,
    [IntegrationSFTPCreateLeadMappingEntry.name]:
      IntegrationSFTPCreateLeadMappingEntry,
  },
  props: {
    service: {
      required: true,
      type: Object,
    },
    requiredMappingFields: {
      default: () => [],
      type: Array,
    },
  },
  emits: ['onAddMappingEntry'],
  setup(props, { emit }) {
    /**
     * Operation configuration
     * @type {RefTypes.ComputedRef<*>}
     */
    const operation = props.service.getOperation()

    /**
     * Collection of lead fields
     * @type {RefTypes.ComputedRef<*>}
     */
    const leadFields = props.service.getServiceData('leadFields')

    /**
     * Collection of lead fields that have not been allocated to a mapping entry
     * @type {ComputedRef<unknown>}
     */
    const leadFieldsUnallocated = computed(() => {
      const usedFields = operation.configuration.mappings.map(
        (mapping) => mapping.config.field,
      )
      return leadFields.value
        .reduce(
          (categories, group) =>
            categories.concat({
              ...group,
              entries: group.entries.filter(
                (field) => !usedFields.includes(field.value),
              ),
            }),
          [],
        )
        .filter((category) => category.entries.length > 0)
    })

    /**
     * Add a new mapping entry
     */
    const addMappingEntry = () => {
      emit('onAddMappingEntry')
    }

    /**
     * Determine if a mapping entry should have a locked target and removal restriction
     * @param {String} field
     * @returns {ComputedRef<boolean>}
     */
    const lockedMappingEntry = (field) =>
      computed(() => props.requiredMappingFields.includes(field))

    /**
     * Additional formatting for a mapping entry
     * @param {Object} mapping
     * @returns {*&{ref: (number|*)}}
     */
    const formatMapping = (mapping) => ({
      ...mapping,
      ref: sourceInput.value === 'number' ? Number(mapping.ref) : mapping.ref,
    })

    /**
     * Update a mapping entry
     * @param {String} uuid
     * @param {Object} mapping
     */
    const updateMappingEntryHandlerImpl = (uuid, mapping) => {
      const idx = operation.configuration.mappings.findIndex(
        (entry) => entry.uuid === uuid,
      )
      if (idx > -1)
        operation.configuration.mappings[idx] = formatMapping(mapping)
    }

    /**
     * Update a mapping entry (debounced)
     * @type {(function(): (any))|*}
     */
    const updateMappingEntryHandlerDebounced = debounce(
      updateMappingEntryHandlerImpl,
      200,
    )

    /**
     * Update a mapping entry
     * @param {String} uuid
     * @param {Object} mapping
     * @param {boolean=} debounced
     */
    const updateMappingEntryHandler = (
      uuid,
      { mapping, debounced = false },
    ) => {
      debounced
        ? updateMappingEntryHandlerDebounced(uuid, mapping)
        : updateMappingEntryHandlerImpl(uuid, mapping)
    }

    /**
     * Delete a mapping entry
     * @param {String} uuid
     */
    const deleteMappingEntryHandler = (uuid) => {
      operation.configuration.mappings =
        operation.configuration.mappings.filter((entry) => entry.uuid !== uuid)
    }

    /**
     * Reference to the source file type
     * @type {ComputedRef<*>}
     */
    const sourceType = computed(() =>
      fileTypes.find(
        (entry) => entry.value === operation.configuration.fileType,
      ),
    )

    /**
     * Reference to the source input type configuration
     * @type {ComputedRef<*>}
     */
    const sourceInputConfig = computed(() => {
      /**
       * Return a fallback configuration
       * @returns {*}
       */
      const referenceWithFallback = (profile) =>
        (profile && profile.config) || { input: 'text', props: {} }

      /**
       * Return a configuration by reference type
       * @param referenceType
       * @returns {*}
       */
      const configByReference = (referenceType) =>
        referenceWithFallback(
          sourceType.value.mappingOptions.find(
            (option) => option.type === referenceType,
          ),
        )

      return (
        (sourceType.value.mappingType === 'reference' &&
          configByReference(operation.configuration.referenceType)) ||
        sourceType.value.mappingOption
      )
    })

    /**
     * Reference to the source file type label
     * @type {ComputedRef<*>}
     */
    const sourceLabel = computed(() => sourceInputConfig.value.label)

    /**
     * Source input type
     * @type {ComputedRef<*>}
     */
    const sourceInput = computed(() => sourceInputConfig.value.input)

    /**
     * Source input additional props
     * @type {ComputedRef<*>}
     */
    const sourceInputProps = computed(() => sourceInputConfig.value.props)

    /**
     * Source input read only state
     * @type {ComputedRef<*>}
     */
    const sourceReadOnly = computed(() => sourceInputConfig.value.readOnly)

    /**
     * Determine if there is enough data to configure mappings
     * @type {ComputedRef<*>}
     */
    const available = computed(() => sourceLabel.value && sourceInput.value)

    return {
      operation,
      leadFields,
      leadFieldsUnallocated,
      lockedMappingEntry,
      addMappingEntry,
      updateMappingEntryHandler,
      deleteMappingEntryHandler,
      sourceLabel,
      sourceInput,
      sourceInputProps,
      sourceReadOnly,
      available,
    }
  },
})
</script>

<style lang="scss" scoped>
@import '@akerolabs/akero-vue/src/scss/_settings.scss';

.mapping {
  &__headers {
    display: grid;
    grid-template-columns: 1fr 1fr 20px;
    gap: $spacing-small;
    color: $color-grey;
    margin-bottom: $spacing-tiny;
    font-size: $font-size-small;
    font-weight: bold;
  }

  &__entry-container + &__entry-container,
  &__actions {
    margin-top: $spacing-small;
  }
}
</style>
